<template>
  <div ref="container" class="chart-graph">
    <canvas ref="chart"></canvas>
  </div>
</template>

<script setup lang="ts">
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { ref, watch } from "vue";
import { useIntersectionObserver } from '@vueuse/core'
import { ChartDataPoint, ChartTimePeriod, PriceChartCurrency } from "@/types/crypto-price-chart";
import { formatNumberStandard } from "@/helpers/numberFormatter";
import { i18n } from "@/i18n";

const props = defineProps<{
  fiatCurrency: PriceChartCurrency,
  datePeriod: ChartTimePeriod,
  chartData: Array<ChartDataPoint>,
}>();


const emit = defineEmits<{
  (event: 'update'): void,
}>();

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
);

const container = ref<HTMLDivElement|null>(null);
const chart = ref<HTMLCanvasElement|null>(null);

let chartObject = null;

const { stop } = useIntersectionObserver(
    container,
    ([{ isIntersecting }]) => {
      if (isIntersecting && chart.value) {
        chartObject = createChart(props.chartData, chart.value);
        emit('update');
        stop();
      }
    },
    { threshold: 0.5 },
);

watch(() => props.chartData, (value) => {
  if (!value.length || !chart.value || !chartObject) {
    return;
  }

  chartObject.data.datasets[0].data = value;
  chartObject.update();
  emit('update');
});

const createChart = (chartData, chartElement) => {
  return new Chart(chartElement, {
    type: 'line',
    data: {
      labels: false,
      datasets: [{
        backgroundColor: ({ chart }) => {
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            // This case happens on initial chart load
            return;
          }
          const gradientFill = ctx.createLinearGradient(0, 0, 0, chartArea.height);
          gradientFill.addColorStop(0, '#d5d6f2');
          gradientFill.addColorStop(1, '#ffffff');

          return gradientFill;
        },
        fill: true,
        data: chartData,
        pointStyle: false,
        borderColor: '#A4A7E3',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
      }],
    },
    options: {
      interaction: {
        intersect: false,
        mode: 'index',
      },
      layout: {
        padding: {
          top: 50,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          usePointStyle: true,
          axis: 'x',
          callbacks: {
            title: (tooltipItemList) => chartMethods.formatStringToLongDate(tooltipItemList[0].label),
            beforeBody: (tooltipItemList) => chartMethods.formatAmountString(tooltipItemList[0].parsed.y),
            label: () => '',
          },
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 20,
          },
        }
      },
      parsing: {
        xAxisKey: 'date',
        yAxisKey: 'price',
      },
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        y: {
          border: {
            display: false,
            dash: [2, 6]
          },
          grid: {
            display: true,
            drawTicks: false,
            lineWidth: 2,
            z: 1,
          },
          ticks: {
            align: 'end',
            padding: 10,
            maxTicksLimit: 5,
            color: '#000000',
            font: {
              size: 16,
              weight: 'bold',
            },
          },
        },
        x: {
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            align: 'start',
            padding: 10,
            maxTicksLimit: 7,
            color: '#000000',
            font: {
              size: 16,
              weight: 'bold',
            },
            callback: function (val) {
              const label = this.getLabelForValue(val);
              return chartMethods.formatStringToShortDate(label);
            },
          },
        },
      },
      resizeDelay: 100,
    },
  });
}

const chartMethods = {
  formatAmountString (amountString: string) {
    return formatNumberStandard(Number(amountString), i18n.locale, props.fiatCurrency.code);
  },
  formatStringToLongDate (dateString: string) {
    const date = new Date(dateString);

    if ([ChartTimePeriod.HOUR, ChartTimePeriod.DAY, ChartTimePeriod.WEEK, ChartTimePeriod.MONTH].includes(props.datePeriod)) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        month: 'short',
        year: 'numeric',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },
  formatStringToShortDate (dateString: string) {
    const date = new Date(dateString);

    if ([ChartTimePeriod.HOUR, ChartTimePeriod.DAY].includes(props.datePeriod)) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
      });
    }
    if ([ChartTimePeriod.WEEK, ChartTimePeriod.MONTH, ChartTimePeriod.THREE_MONTHS, ChartTimePeriod.SIX_MONTHS].includes(props.datePeriod)) {
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
    });
  },
};

</script>

<style scoped lang="scss">
.chart-graph {
  height: 400px;
}
</style>